<template>
  <main class="bills-overview-page">
    <!-- Perspective tabs -->
    <el-tabs v-model="perspective" @tab-click="onPerspectiveChange">
      <el-tab-pane label="供应商视角" name="supplier" />
      <el-tab-pane label="客户视角" name="customer" />
    </el-tabs>

    <!-- Period tabs -->
    <el-tabs v-model="activePeriod" @tab-click="onPeriodChange">
      <el-tab-pane label="年度" name="annual" />
      <el-tab-pane label="月度" name="monthly" />
    </el-tabs>

    <!-- Summary cards -->
    <section class="summary-cards">
      <div v-for="card in cards" :key="card.label" class="summary-card">
        <div class="card-label">{{ card.label }}</div>
        <div class="card-value" :class="card.cls">{{ card.value }}</div>
      </div>
    </section>

    <!-- Comparison charts -->
    <section class="charts-grid">
      <div class="soft-card chart-panel">
        <div class="panel-head">
          <h3 class="panel-title">{{ barTitle }}</h3>
          <span class="panel-sub">{{ rangeLabel }}</span>
        </div>
        <div ref="barChart" class="chart-container" />
      </div>
      <div class="soft-card chart-panel">
        <div class="panel-head">
          <h3 class="panel-title">{{ pieTitle }}</h3>
          <span class="panel-sub">{{ rangeLabel }}</span>
        </div>
        <div ref="pieChart" class="chart-container" />
      </div>
    </section>

    <!-- Entities table -->
    <section class="soft-card table-panel">
      <div class="toolbar">
        <span class="toolbar-title">{{ tableTitle }}</span>
        <el-date-picker
          v-if="activePeriod === 'annual'"
          v-model="pickerYear"
          type="year"
          placeholder="选择年份"
          size="small"
          value-format="yyyy"
          class="date-picker"
          @change="onDateChange"
        />
        <el-date-picker
          v-else
          v-model="pickerMonth"
          type="month"
          placeholder="选择月份"
          size="small"
          value-format="yyyy-MM"
          class="date-picker"
          @change="onDateChange"
        />
        <el-button size="small" icon="el-icon-download" @click="onExport">导出</el-button>
      </div>
      <el-table :data="pagedRows" size="small" style="width: 100%" v-loading="loading">
        <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
        <el-table-column :label="entityNameLabel" min-width="140">
          <template slot-scope="scope">
            <span class="entity-name">{{ scope.row.name || scope.row.code }}</span>
          </template>
        </el-table-column>
        <el-table-column label="调用次数" align="right" min-width="90">
          <template slot-scope="scope">{{ $formatNumber(scope.row.total_calls) }}</template>
        </el-table-column>
        <el-table-column label="总 Token" align="right" min-width="110">
          <template slot-scope="scope">{{ $formatNumber(scope.row.total_tokens) }}</template>
        </el-table-column>
        <el-table-column :label="incomeLabel" align="right" min-width="120">
          <template slot-scope="scope">¥ {{ $formatNumber(scope.row.total_cost) }}</template>
        </el-table-column>
        <el-table-column :label="costLabel" align="right" min-width="120">
          <template slot-scope="scope">
            <span class="cost-amount">¥ {{ $formatNumber(scope.row.supplier_cost) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="利润" align="right" min-width="120">
          <template slot-scope="scope">
            <span :class="scope.row.profit >= 0 ? 'profit-positive' : 'profit-negative'">
              ¥ {{ $formatNumber(scope.row.profit) }}
              <small class="profit-rate">({{ profitRate(scope.row) }})</small>
            </span>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
    </section>
  </main>
</template>

<script>
import * as echarts from 'echarts'
import * as XLSX from 'xlsx'

const PIE_COLORS = ['#55f0b3', '#32c8f5', '#ffb020', '#9f8bff', '#ff6b7a', '#4facfe', '#ff87d4', '#5ad8a6']

export default {
  name: 'BillsOverviewPage',
  data() {
    const now = new Date()
    return {
      perspective: 'supplier',
      activePeriod: 'annual',
      pickerYear: String(now.getFullYear()),
      pickerMonth: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0'),
      summary: {
        entity_count: 0,
        total_tokens: 0,
        total_cost: 0,
        supplier_cost: 0,
        profit: 0,
        total_calls: 0
      },
      entities: [],
      pageSize: 10,
      currentPage: 1,
      loading: false,
      barChart: null,
      pieChart: null
    }
  },
  computed: {
    isSupplier() {
      return this.perspective === 'supplier'
    },
    entityNameLabel() {
      return this.isSupplier ? '供应商' : '客户'
    },
    incomeLabel() {
      return this.isSupplier ? '计费收入' : '客户费用'
    },
    costLabel() {
      return this.isSupplier ? '应付成本' : '供应商成本'
    },
    barTitle() {
      return this.isSupplier ? '收入 vs 应付成本（按供应商）' : '费用 vs 供应商成本（按客户）'
    },
    pieTitle() {
      return this.isSupplier ? '应付成本占比' : '客户费用占比'
    },
    rangeLabel() {
      return this.activePeriod === 'annual' ? this.pickerYear + '年' : this.pickerMonth
    },
    tableTitle() {
      const periodTitle = this.activePeriod === 'annual' ? '年度' : '月度'
      return this.isSupplier ? '供应商' + periodTitle + '账单' : '客户' + periodTitle + '账单'
    },
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    pagedRows() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.entities.slice(start, start + this.pageSize)
    },
    total() {
      return this.entities.length || 0
    },
    cards() {
      const s = this.summary
      const first = { label: this.isSupplier ? '供应商数' : '客户数', value: this.$formatNumber(s.entity_count), cls: '' }
      if (this.isSupplier) {
        return [
          first,
          { label: '总调用', value: this.$formatNumber(s.total_calls), cls: '' },
          { label: '总 Token', value: this.$formatNumber(s.total_tokens), cls: '' },
          { label: '计费收入', value: '¥ ' + this.$formatNumber(s.total_cost), cls: 'cost-color' },
          { label: '应付成本', value: '¥ ' + this.$formatNumber(s.supplier_cost), cls: 'muted-color' },
          { label: '利润', value: '¥ ' + this.$formatNumber(s.profit), cls: (s.profit || 0) >= 0 ? 'profit-positive' : 'profit-negative' }
        ]
      }
      return [
        first,
        { label: '总调用', value: this.$formatNumber(s.total_calls), cls: '' },
        { label: '总 Token', value: this.$formatNumber(s.total_tokens), cls: '' },
        { label: '客户费用', value: '¥ ' + this.$formatNumber(s.total_cost), cls: 'cost-color' },
        { label: '供应商成本', value: '¥ ' + this.$formatNumber(s.supplier_cost), cls: 'muted-color' },
        { label: '利润', value: '¥ ' + this.$formatNumber(s.profit), cls: (s.profit || 0) >= 0 ? 'profit-positive' : 'profit-negative' }
      ]
    }
  },
  mounted() {
    this.fetchAll()
    this.resetChartsOnResize()
  },
  beforeDestroy() {
    this.disposeCharts()
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    apiRoot() {
      return this.isSupplier ? '/admin/billing/supplier' : '/admin/billing/customer'
    },
    buildParams() {
      const params = { period: this.activePeriod }
      if (this.activePeriod === 'annual') {
        params.year = parseInt(this.pickerYear) || new Date().getFullYear()
      } else {
        const parts = (this.pickerMonth || '').split('-')
        params.year = parseInt(parts[0]) || new Date().getFullYear()
        params.month = parseInt(parts[1]) || (new Date().getMonth() + 1)
      }
      return params
    },
    async fetchAll() {
      this.loading = true
      try {
        const params = this.buildParams()
        // 分页从后端全量取数，图表/表格/导出共用一份完整数据
        const [summary, entities] = await Promise.all([
          this.$http.get(this.apiRoot() + '/summary', { params }),
          this.$http.get(this.apiRoot() + '/entities', { params })
        ])
        this.summary = summary || {}
        this.entities = entities || []
        this.currentPage = 1
        this.$nextTick(() => this.renderCharts())
      } catch {
        this.$message.error('加载账单数据失败')
      } finally {
        this.loading = false
      }
    },
    onPerspectiveChange() {
      this.resetCharts()
      this.currentPage = 1
      this.fetchAll()
    },
    onPeriodChange() {
      this.currentPage = 1
      this.fetchAll()
    },
    onDateChange() {
      this.currentPage = 1
      this.fetchAll()
    },
    onPageChange() {
      // 客户端分页
    },
    profitRate(row) {
      if (!row.total_cost) return '0%'
      const rate = ((Number(row.profit) || 0) / Number(row.total_cost)) * 100
      return (rate >= 0 ? '+' : '') + rate.toFixed(1) + '%'
    },
    onExport() {
      if (!this.entities.length) {
        this.$message.info('没有数据可导出')
        return
      }
      const data = this.entities.map(r => ({
        [this.entityNameLabel]: r.name || r.code,
        '调用次数': r.total_calls,
        '总Token': r.total_tokens,
        [this.incomeLabel]: r.total_cost,
        [this.costLabel]: r.supplier_cost,
        '利润': r.profit,
        '利润率': this.profitRate(r)
      }))
      const ws = XLSX.utils.json_to_sheet(data)
      const wb = XLSX.utils.book_new()
      const title = this.tableTitle
      XLSX.utils.book_append_sheet(wb, ws, title)
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      const blob = new Blob([wbout], { type: 'application/octet-stream' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const ts = new Date()
      const pad = n => String(n).padStart(2, '0')
      a.download = `${title}_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.xlsx`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      this.$message.success('导出成功')
    },
    // ==================== 图表 ====================
    shortName(name) {
      const s = String(name || '')
      return s.length > 10 ? s.substring(0, 10) + '…' : s
    },
    renderCharts() {
      this.$nextTick(() => {
        this.renderBarChart()
        this.renderPieChart()
      })
    },
    renderBarChart() {
      const el = this.$refs.barChart
      if (!el) return
      if (this.barChart) this.barChart.dispose()
      if (!this.entities.length) {
        this.barChart = echarts.init(el)
        this.barChart.setOption({
          title: { text: '暂无数据', textStyle: { color: '#9aaac1', fontSize: 14 }, left: 'center', top: 'middle' }
        })
        return
      }
      const chart = echarts.init(el)
      this.barChart = chart
      const names = this.entities.map(r => this.shortName(r.name || r.code))
      const incomes = this.entities.map(r => Number(r.total_cost) || 0)
      const costs = this.entities.map(r => Number(r.supplier_cost) || 0)

      chart.setOption({
        color: ['#55f0b3', '#ffb020'],
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(20,25,40,0.92)',
          borderColor: 'rgba(133,164,255,0.2)',
          textStyle: { color: '#c8d0e0', fontSize: 12 },
          formatter(params) {
            const rows = params.map(p => `${p.marker} ${p.seriesName}&nbsp;&nbsp;¥ ${Number(p.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`)
            return params[0].name + '<br/>' + rows.join('<br/>')
          }
        },
        legend: {
          top: 6,
          right: 10,
          textStyle: { color: '#9aaac1', fontSize: 12 },
          data: [this.incomeLabel, this.costLabel]
        },
        grid: { left: 70, right: 20, top: 40, bottom: 40 },
        xAxis: {
          type: 'category',
          data: names,
          axisLine: { lineStyle: { color: 'rgba(133,164,255,0.15)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, rotate: names.length > 6 ? 30 : 0 }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(133,164,255,0.08)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, formatter: v => '¥' + v }
        },
        series: [
          { name: this.incomeLabel, type: 'bar', data: incomes, barMaxWidth: 26, itemStyle: { borderRadius: [4, 4, 0, 0] } },
          { name: this.costLabel, type: 'bar', data: costs, barMaxWidth: 26, itemStyle: { borderRadius: [4, 4, 0, 0] } }
        ]
      })
    },
    renderPieChart() {
      const el = this.$refs.pieChart
      if (!el) return
      if (this.pieChart) this.pieChart.dispose()
      if (!this.entities.length) {
        this.pieChart = echarts.init(el)
        this.pieChart.setOption({
          title: { text: '暂无数据', textStyle: { color: '#9aaac1', fontSize: 14 }, left: 'center', top: 'middle' }
        })
        return
      }
      const key = this.isSupplier ? 'supplier_cost' : 'total_cost'
      const chart = echarts.init(el)
      this.pieChart = chart
      const data = this.entities
        .map(r => ({ name: this.shortName(r.name || r.code), value: Number(r[key]) || 0 }))
        .filter(d => d.value > 0)

      chart.setOption({
        color: PIE_COLORS,
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(20,25,40,0.92)',
          borderColor: 'rgba(133,164,255,0.2)',
          textStyle: { color: '#c8d0e0', fontSize: 12 },
          formatter: '{b}<br/>¥ {c}（{d}%）'
        },
        legend: {
          orient: 'vertical',
          right: 10,
          top: 'middle',
          textStyle: { color: '#9aaac1', fontSize: 11 },
          formatter: name => name
        },
        series: [{
          type: 'pie',
          radius: ['42%', '68%'],
          center: ['38%', '50%'],
          avoidLabelOverlap: true,
          itemStyle: { borderRadius: 6, borderColor: 'rgba(13,21,34,0.9)', borderWidth: 2 },
          label: { color: '#c8d0e0', fontSize: 11, formatter: '{d}%' },
          labelLine: { lineStyle: { color: 'rgba(133,164,255,0.35)' } },
          data
        }]
      })
    },
    resetCharts() {
      if (this.barChart) { this.barChart.dispose(); this.barChart = null }
      if (this.pieChart) { this.pieChart.dispose(); this.pieChart = null }
    },
    disposeCharts() {
      this.resetCharts()
    },
    onResize() {
      if (this.barChart) this.barChart.resize()
      if (this.pieChart) this.pieChart.resize()
    },
    resetChartsOnResize() {
      window.addEventListener('resize', this.onResize)
    }
  }
}
</script>

<style scoped>
.bills-overview-page {
  padding: 0 24px 76px;
}

.bills-overview-page ::v-deep .el-tabs__header:first-child {
  border-bottom: 1px solid rgba(133, 164, 255, 0.15);
  margin-bottom: 6px;
}

.bills-overview-page ::v-deep .el-tabs__header {
  border-bottom: 1px solid rgba(133, 164, 255, 0.15);
  margin-bottom: 20px;
}

.bills-overview-page ::v-deep .el-tabs__item {
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 800;
  height: 44px;
  line-height: 44px;
  letter-spacing: 0.02em;
}

.bills-overview-page ::v-deep .el-tabs__item:hover {
  color: var(--text);
}

.bills-overview-page ::v-deep .el-tabs__item.is-active {
  color: var(--text);
}

.bills-overview-page ::v-deep .el-tabs__active-bar {
  background: #55f0b3;
  height: 2.5px;
  border-radius: 2px;
}

.bills-overview-page ::v-deep .el-tabs__nav-wrap::after {
  background: transparent;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.summary-card {
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}

.card-label {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 8px;
  font-weight: 760;
}

.card-value {
  font-size: 19px;
  font-weight: 900;
  color: var(--text);
  line-height: 1.1;
}

.cost-color { color: #34c98f; }
.muted-color { color: var(--muted-2); }
.profit-positive { color: #34c98f; }
.profit-negative { color: #ff6b7a; }

.charts-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.chart-panel {
  padding: 16px;
}

.chart-container {
  width: 100%;
  height: 300px;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.panel-title {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: var(--text);
}

.panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.entity-name {
  color: var(--text);
  font-weight: 700;
}

.cost-amount {
  color: var(--amber);
}

.profit-rate {
  color: var(--muted);
  font-size: 11px;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.toolbar-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--text);
  flex: 1;
}

.date-picker {
  width: 160px;
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

@media (max-width: 900px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
}
</style>